import React, { useRef, useEffect } from 'react'
import {
  Activity,
  Brain,
  Wrench,
  CheckCircle,
  MessageSquare,
  Play,
  ArrowRight,
  Cpu,
  User,
  Bot,
  Zap
} from 'lucide-react'
import type { RawEvent, ExecutionTrace } from './executionData'

interface ExecutionVisualizerProps {
  events: RawEvent[]
  currentTime: number
  execution?: ExecutionTrace
}

export const ExecutionVisualizer: React.FC<ExecutionVisualizerProps> = ({
  events,
  currentTime,
}) => {
  // Filter out redundant events:
  // - ToolCallResponseEvent: ToolMessage shows the same info
  // - ModelResponseEvent with has_tool_calls: ToolCallEvent shows the actual call
  const filteredEvents = events.filter(e => {
    if (e._class === 'ToolCallResponseEvent') return false
    if (e._class === 'ModelResponseEvent' && e.has_tool_calls) return false
    return true
  })
  const visibleEvents = filteredEvents.filter(e => e._elapsed_ms <= currentTime)
  const containerRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom within the scrollable parent container
  useEffect(() => {
    if (containerRef.current && visibleEvents.length > 0) {
      // Find the scrollable parent (the one with overflow-auto)
      const scrollableParent = containerRef.current.parentElement
      if (scrollableParent) {
        scrollableParent.scrollTop = scrollableParent.scrollHeight
      }
    }
  }, [visibleEvents.length])

  const getEventIcon = (event: RawEvent) => {
    const eventClass = event._class

    // Message types
    if (eventClass === 'UserMessage') return <User className="w-4 h-4" />
    if (eventClass === 'AssistantMessage') return <Bot className="w-4 h-4" />
    if (eventClass === 'ToolMessage') return <Zap className="w-4 h-4" />

    // Agent events
    if (eventClass === 'TaskStartEvent') return <Play className="w-4 h-4" />
    if (eventClass === 'TaskCompleteEvent') return <CheckCircle className="w-4 h-4" />
    if (eventClass === 'ModelCallEvent') return <Cpu className="w-4 h-4" />
    if (eventClass === 'ModelResponseEvent') return <Brain className="w-4 h-4" />
    if (eventClass === 'ModelStreamChunkEvent') return <MessageSquare className="w-4 h-4" />
    if (eventClass === 'ToolCallEvent') return <Wrench className="w-4 h-4" />
    if (eventClass === 'ToolCallResponseEvent') return <Zap className="w-4 h-4" />
    if (eventClass === 'AgentResponse') return <CheckCircle className="w-4 h-4" />

    // Workflow events
    if (eventClass === 'WorkflowStartedEvent') return <Play className="w-4 h-4" />
    if (eventClass === 'WorkflowCompletedEvent') return <CheckCircle className="w-4 h-4" />
    if (eventClass === 'StepStartedEvent') return <Activity className="w-4 h-4" />
    if (eventClass === 'StepCompletedEvent') return <CheckCircle className="w-4 h-4" />
    if (eventClass === 'EdgeActivatedEvent') return <ArrowRight className="w-4 h-4" />

    // Orchestration events
    if (eventClass === 'OrchestrationStartEvent') return <Play className="w-4 h-4" />
    if (eventClass === 'OrchestrationCompleteEvent') return <CheckCircle className="w-4 h-4" />
    if (eventClass === 'AgentSelectionEvent') return <Bot className="w-4 h-4" />
    if (eventClass === 'AgentExecutionStartEvent') return <Play className="w-4 h-4" />
    if (eventClass === 'AgentExecutionCompleteEvent') return <CheckCircle className="w-4 h-4" />
    if (eventClass === 'OrchestrationResponse') return <CheckCircle className="w-4 h-4" />

    return <Activity className="w-4 h-4" />
  }

  const getEventStyles = (event: RawEvent) => {
    const eventClass = event._class

    // Message types
    if (eventClass === 'UserMessage') {
      return 'bg-blue-500/10 border-blue-500/30 text-blue-600 dark:text-blue-400'
    }
    if (eventClass === 'AssistantMessage') {
      return 'bg-indigo-500/10 border-indigo-500/30 text-indigo-600 dark:text-indigo-400'
    }
    if (eventClass === 'ToolMessage') {
      return 'bg-green-500/10 border-green-500/30 text-green-600 dark:text-green-400'
    }

    // Start/End events
    if (eventClass.includes('Start') || eventClass.includes('Started')) {
      return 'bg-blue-500/10 border-blue-500/30 text-blue-600 dark:text-blue-400'
    }
    if (eventClass.includes('Complete') || eventClass.includes('Completed') || eventClass === 'AgentResponse') {
      return 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400'
    }

    // Model events
    if (eventClass === 'ModelCallEvent') {
      return 'bg-purple-500/10 border-purple-500/30 text-purple-600 dark:text-purple-400'
    }
    if (eventClass === 'ModelResponseEvent' || eventClass === 'ModelStreamChunkEvent') {
      return 'bg-violet-500/10 border-violet-500/30 text-violet-600 dark:text-violet-400'
    }

    // Tool events
    if (eventClass === 'ToolCallEvent') {
      return 'bg-orange-500/10 border-orange-500/30 text-orange-600 dark:text-orange-400'
    }
    if (eventClass === 'ToolCallResponseEvent') {
      return 'bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400'
    }

    // Workflow flow
    if (eventClass === 'EdgeActivatedEvent') {
      return 'bg-cyan-500/10 border-cyan-500/30 text-cyan-600 dark:text-cyan-400'
    }

    // Agent selection
    if (eventClass === 'AgentSelectionEvent') {
      return 'bg-pink-500/10 border-pink-500/30 text-pink-600 dark:text-pink-400'
    }

    return 'bg-secondary/10 border-secondary/30 text-secondary'
  }

  const getEventContent = (event: RawEvent): string => {
    // Show actual data from JSON - no made up text
    if (event.content) return event.content
    if (event.task) return event.task
    if (event.response) return event.response
    if (event.chunk) return event.chunk
    if (event.tool_name) {
      const params = event.parameters ? JSON.stringify(event.parameters) : ''
      return `${event.tool_name}(${params})`
    }
    if (event.result) {
      if (typeof event.result === 'string') return event.result
      return event.result.result || ''
    }
    if (event.initial_input) return JSON.stringify(event.initial_input)
    if (event.output_data) return JSON.stringify(event.output_data)
    if (event.step_id) return event.step_id
    if (event.from_step && event.to_step) return `${event.from_step} → ${event.to_step}`
    if (event.selected_agent) return event.selected_agent
    if (event.executing_agent) return event.executing_agent
    if (event.stop_reason) return event.stop_reason
    if (event.final_result) return event.final_result
    if (event.usage) {
      return `${event.usage.llm_calls} LLM calls, ${event.usage.tool_calls} tool calls, ${(event.usage.duration_ms / 1000).toFixed(1)}s`
    }
    if (event.model) return event.model
    return ''
  }

  const getEventLabel = (event: RawEvent): string => {
    const eventClass = event._class

    // Friendlier labels
    if (eventClass === 'UserMessage') return 'User'
    if (eventClass === 'AssistantMessage') return 'Assistant'
    if (eventClass === 'ToolMessage') return 'Tool Result'
    if (eventClass === 'TaskStartEvent') return 'Task Start'
    if (eventClass === 'TaskCompleteEvent') return 'Task Complete'
    if (eventClass === 'ModelCallEvent') return 'Model Call'
    if (eventClass === 'ModelResponseEvent') return 'Model Response'
    if (eventClass === 'ModelStreamChunkEvent') return 'Streaming'
    if (eventClass === 'ToolCallEvent') return 'Tool Call'
    if (eventClass === 'ToolCallResponseEvent') return 'Tool Response'
    if (eventClass === 'AgentResponse') return 'Complete'
    if (eventClass === 'WorkflowStartedEvent') return 'Workflow Start'
    if (eventClass === 'WorkflowCompletedEvent') return 'Workflow Complete'
    if (eventClass === 'StepStartedEvent') return 'Step Start'
    if (eventClass === 'StepCompletedEvent') return 'Step Complete'
    if (eventClass === 'EdgeActivatedEvent') return 'Data Flow'
    if (eventClass === 'OrchestrationStartEvent') return 'Orchestration Start'
    if (eventClass === 'OrchestrationCompleteEvent') return 'Orchestration Complete'
    if (eventClass === 'AgentSelectionEvent') return 'Agent Selected'
    if (eventClass === 'AgentExecutionStartEvent') return 'Agent Start'
    if (eventClass === 'AgentExecutionCompleteEvent') return 'Agent Done'
    if (eventClass === 'OrchestrationResponse') return 'Result'

    return eventClass
  }

  return (
    <div ref={containerRef} className="p-4">
      {/* Event timeline */}
      <div className="space-y-2">
        {visibleEvents.map((event, index) => (
          <div
            key={index}
            className={`
              flex items-start space-x-3 p-3 rounded-lg border
              ${getEventStyles(event)}
              animate-fade-in
            `}
            style={{
              animationDelay: `${index * 30}ms`,
            }}
          >
            <div className="flex-shrink-0 mt-0.5">
              {getEventIcon(event)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold uppercase opacity-70">
                  {getEventLabel(event)}
                </span>
                {event.source && (
                  <span className="text-xs opacity-50">
                    [{event.source}]
                  </span>
                )}
              </div>
              <div className="text-sm text-primary">
                {getEventContent(event)}
              </div>
            </div>
            <div className="flex-shrink-0 text-xs text-secondary">
              {(event._elapsed_ms / 1000).toFixed(2)}s
            </div>
          </div>
        ))}
      </div>

      {/* Waiting indicator */}
      {visibleEvents.length === 0 && (
        <div className="text-center py-12 text-secondary">
          <Activity className="w-8 h-8 mx-auto mb-3 opacity-50" />
          <p className="text-sm">Click "Play" to start the execution</p>
        </div>
      )}

      {/* Add CSS for animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  )
}
